import { useApolloClient } from '@apollo/client';
import { useTranslations } from '@vocab/react';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import * as Schema from 'src/types/graphql';
import { GetProfileVisibility2Document } from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import { useProfileVisibilityOptions } from './EditProfileVisibility/data';
import { generateUpdateProfileVisibilityOptimisticResponse2 } from './apolloCache.optimistic';
import type { ProfileVisibility2 } from './types';

export const useGetProfileVisibility = () => {
  const { language } = useAppConfig();
  const { data, loading, error } = useQuery(
    Schema.GetProfileVisibilityDocument,
    {
      variables: { languageCode: language },
    },
  );

  const profileVisibilityLevel = data?.viewer?.profileVisibility?.level || null;

  return { profileVisibilityLevel, loading, error };
};

export const useGetProfileVisibility2 = () => {
  const { language, locale } = useAppConfig();
  const languageLocale = `${language}-${locale}`;
  const { data, loading, error } = useQuery(
    Schema.GetProfileVisibility2Document,
    {
      variables: { locale: languageLocale },
    },
  );

  const profileVisibility2 = data?.viewer?.profileVisibility2 ?? undefined;

  return { profileVisibility2, loading, error };
};

export const mockGetProfileVisibility2Request = (
  profileVisibility2: ProfileVisibility2,
) => ({
  request: {
    query: GetProfileVisibility2Document,
    variables: { locale: 'en-AU' },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        profileVisibility2,
      },
    },
  },
});

export const useUpdateProfileVisibility2 = () => {
  const client = useApolloClient();
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const { profileVisibilityOptions } = useProfileVisibilityOptions();

  const onCompleted = (data: Schema.UpdateProfileVisibility2Mutation) => {
    if (
      data.updateProfileVisibility2.__typename ===
        'UpdateProfileVisibility2Success' &&
      data.updateProfileVisibility2.viewer?.profileVisibility2?.id === 'Public'
    ) {
      client.refetchQueries({ include: ['GetPublicProfile'] });
    }
  };

  return useToastMutation(Schema.UpdateProfileVisibility2Document, {
    key: 'profile-visibility',
    errorActionRouteParameters: { card: 'profile-visibility', mode: 'EDIT' },
    optimisticResponse: generateUpdateProfileVisibilityOptimisticResponse2(
      candidateId ?? '',
      profileVisibilityOptions ?? [],
    ),
    successToastMessage: t('Profile visibility updated'),
    failToastMessage: t('Profile visibility could not be updated'),
    onCompleted,
  });
};
